import { useEffect, useState } from "react";
import { storefrontOrigin } from "../config";
import { withSpan } from "../otel";
import { signIn } from "../session";
import { UserRow } from "../UserRow";
import { fetchUsers, generateUsers, type AdminUser } from "../users";

type UsersState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; users: AdminUser[] };

/**
 * Generate/roles/impersonate (#261, ADR 0062). Generate has no form, per
 * the ticket — a count and a button. The impersonate column only shows when
 * the storefront's origin is configured (storefrontOrigin, written to
 * config.json at deploy) — impersonating opens the real storefront as that
 * customer, a cross-origin handoff nothing here can do without it.
 */
export function UsersScreen() {
  const [state, setState] = useState<UsersState>({ status: "loading" });
  const [count, setCount] = useState("5");
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const showImpersonate = storefrontOrigin() !== "";

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setState({ status: "loading" });
    const result = await fetchUsers();
    if (result.kind === "ok") {
      setState({ status: "ready", users: result.value });
    } else if (result.kind === "unauthorized") {
      signIn(window.location.pathname);
    } else {
      setState({ status: "error", message: result.message });
    }
  }

  async function doGenerate() {
    const n = Number(count);
    if (!Number.isInteger(n) || n < 1) {
      setGenerateError("must be a whole number, 1 or more");
      return;
    }

    setGenerating(true);
    setGenerateError(null);
    const result = await withSpan("users.generate", { "users.count": n }, () => generateUsers(n));
    setGenerating(false);

    if (result.kind === "ok") {
      void load(); // re-fetch rather than splice in the response — roles can shift underneath.
    } else if (result.kind === "unauthorized") {
      signIn(window.location.pathname);
    } else {
      setGenerateError(result.message);
    }
  }

  function replaceUser(updated: AdminUser) {
    setState((s) => (s.status === "ready" ? { ...s, users: s.users.map((u) => (u.sub === updated.sub ? updated : u)) } : s));
  }

  return (
    <section>
      <div className="users-head">
        <h2>Users</h2>
        <div className="users-generate">
          <label htmlFor="generate-count">Generate</label>
          <input
            id="generate-count"
            type="text"
            inputMode="numeric"
            value={count}
            disabled={generating}
            onChange={(e) => {
              setCount(e.target.value);
              setGenerateError(null);
            }}
          />
          <button type="button" onClick={doGenerate} disabled={generating}>
            {generating ? "Generating…" : "Generate"}
          </button>
        </div>
      </div>
      {generateError && <p className="field-error">{generateError}</p>}

      {state.status === "loading" && <p className="status-message">Loading users…</p>}

      {state.status === "error" && (
        <>
          <p className="status-error">Couldn't load users ({state.message}).</p>
          <button type="button" onClick={() => void load()}>
            Retry
          </button>
        </>
      )}

      {state.status === "ready" && (
        <table className="users-table">
          <thead>
            <tr>
              <th>sub</th>
              <th>name</th>
              <th>email</th>
              <th>role</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {state.users.map((u) => (
              <UserRow key={u.sub} user={u} showImpersonate={showImpersonate} onUpdated={replaceUser} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
